/**
 * QuotesPage.jsx — Quotes with tab navigation: Browser + Builder + Pay Split
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import QuoteBuilder from '../components/quotes/QuoteBuilder';
import QuoteBrowser from '../components/quotes/QuoteBrowser';
import CalculatorPage from './CalculatorPage';
import CompetitionPriceListDialog from '../components/quotes/CompetitionPriceListDialog';
import { useMobile } from '../hooks/useMobile';
import { useFocus } from '../hooks/useFocus';

const VALID_TABS = ['builder', 'browser', 'calculator'];

export default function QuotesPage() {
  const [tab, setTab] = useState('builder');
  const [showCompetition, setShowCompetition] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useMobile();
  const { isHidden } = useFocus();

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && VALID_TABS.includes(t)) { setTab(t); searchParams.delete('tab'); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams, setSearchParams]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100dvh - 84px)' : 'calc(100dvh - 8rem)', gap: 1 }}>
      <div className="flex items-center gap-2 md:gap-6 flex-wrap">
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>Quotes</Typography>
        <ToggleButtonGroup size="small" value={tab} exclusive onChange={(_, v) => { if (v) setTab(v); }} sx={{ flexWrap: 'nowrap' }}>
          <ToggleButton value="builder" sx={{ fontSize: '0.75rem', px: { xs: 1, sm: 2 } }}>Builder</ToggleButton>
          <ToggleButton value="browser" sx={{ fontSize: '0.75rem', px: { xs: 1, sm: 2 } }}>Saved Quotes</ToggleButton>
          {(!isHidden('quotes.paySplitTab') || tab === 'calculator') && (
            <ToggleButton value="calculator" data-focus="quotes.paySplitTab" sx={{ fontSize: '0.75rem', px: { xs: 1, sm: 2 } }}>Pay Split</ToggleButton>
          )}
        </ToggleButtonGroup>
        {/* Our New-list prices against competitors at any size, no quote needed */}
        <Button size="small" variant="outlined" onClick={() => setShowCompetition(true)} sx={{ ml: 'auto', fontSize: '0.75rem' }}>
          Competition analysis
        </Button>
      </div>
      {showCompetition && <CompetitionPriceListDialog onClose={() => setShowCompetition(false)} />}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {tab === 'builder' && <QuoteBuilder />}
        {tab === 'browser' && <QuoteBrowser />}
        {tab === 'calculator' && <CalculatorPage embedded />}
      </Box>
    </Box>
  );
}
